import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import {
  Dashboard as GrafanaDashboardIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { GrafanaPreviewProps, getCardBorder, CARD_BORDER_RADIUS } from '../types';

/**
 * GrafanaPreview - Embeds a Grafana dashboard preview with controls.
 *
 * Features:
 * - Embedded Grafana iframe (or placeholder in Storybook/dev mode)
 * - Header with title and fullscreen button
 * - Responsive height based on viewport
 *
 * Note: In Storybook/development, shows a placeholder instead of real iframe.
 */
export function GrafanaPreview({
  url,
  deviceName,
  height = 400,
  showPlaceholder = false,
  onFullscreen,
  sx,
}: GrafanaPreviewProps) {
  const theme = useTheme();
  const heightValue = typeof height === 'number' ? height : parseInt(height as string, 10) || 400;

  return (
    <Paper
      elevation={0}
      sx={{
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        overflow: 'hidden',
        height: '100%',
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: getCardBorder(theme),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GrafanaDashboardIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            Dashboard Preview
          </Typography>
        </Box>
        {onFullscreen && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={onFullscreen}
          >
            Full Screen
          </Button>
        )}
      </Box>

      {/* Grafana Iframe or Placeholder */}
      <Box
        sx={{
          height: heightValue,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {showPlaceholder ? (
          // Development/Storybook placeholder
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <GrafanaDashboardIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Grafana Dashboard Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              In production, this would show an embedded Grafana dashboard for:
            </Typography>
            <Chip label={deviceName} size="small" sx={{ mb: 1 }} />
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              fontFamily="monospace"
              sx={{ fontSize: '0.7rem' }}
            >
              {url}
            </Typography>
          </Box>
        ) : (
          // Production iframe
          <iframe
            src={url}
            title={`Grafana Dashboard - ${deviceName}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock allow-downloads"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          />
        )}
      </Box>
    </Paper>
  );
}

export default GrafanaPreview;
